/* AgentMeeting — Changelog (release-notes timeline) */

import React from 'react';
import { useI18n } from '../shared/i18n';
import { MarketingBar, MarketingFooter } from '../shared/marketing-bar';

export function Changelog({ goTo }) {
  const { lang } = useI18n();
  const releases = lang === 'zh' ? CHANGELOG_ZH : CHANGELOG_EN;
  const [filter, setFilter] = React.useState('all');
  const filters = lang === 'zh'
    ? [['all','全部'],['feature','功能'],['fix','修复'],['breaking','变更'],['model','模型']]
    : [['all','All'],['feature','Features'],['fix','Fixes'],['breaking','Breaking'],['model','Models']];

  const filtered = filter === 'all'
    ? releases
    : releases.map(r => ({ ...r, items: r.items.filter(i => i.kind === filter) }))
              .filter(r => r.items.length > 0);

  return (
    <div className="mkt-page">
      <MarketingBar here="changelog" goTo={goTo} signedIn={false} />

      <section style={{ padding: '64px 28px 32px' }}>
        <div style={{ maxWidth: 880, margin: '0 auto' }}>
          <div className="mono tiny" style={{ color: 'var(--live)', letterSpacing: '0.16em' }}>
            [CHANGELOG]
          </div>
          <h1 style={{
            fontSize: 'clamp(34px, 4.4vw, 52px)',
            margin: '8px 0 14px', letterSpacing: '-0.025em', fontWeight: 500,
          }}>
            {lang === 'zh' ? '我们改了什么' : 'What we shipped'}
          </h1>
          <p style={{
            fontSize: 16, color: 'var(--fg-1)', lineHeight: 1.6,
            maxWidth: 640, margin: 0,
          }}>
            {lang === 'zh'
              ? '每周一更 · 大改动单独发 RFC · 不破坏老 agenda 的兼容性。'
              : 'Weekly cadence · large changes ship behind RFCs · we never break existing agendas.'}
          </p>

          {/* subscribe row */}
          <div className="flex gap-2 wrap" style={{ marginTop: 24 }}>
            <a className="tag mono" style={{ borderColor: 'var(--line-3)', cursor: 'pointer' }}>
              📨 {lang === 'zh' ? '邮件订阅' : 'subscribe via email'}
            </a>
            <a className="tag mono" style={{ borderColor: 'var(--line-3)', cursor: 'pointer' }}>
              📡 RSS feed
            </a>
            <a className="tag mono" style={{ borderColor: 'var(--line-3)', cursor: 'pointer' }}>
              𝕏 @agentmeeting
            </a>
            <a className="tag mono" style={{ borderColor: 'var(--line-3)', cursor: 'pointer' }}>
              GitHub releases ↗
            </a>
          </div>

          {/* filters */}
          <div className="flex gap-1" style={{
            marginTop: 32, paddingBottom: 16,
            borderBottom: '1px solid var(--line-1)',
            flexWrap: 'wrap',
          }}>
            {filters.map(([k, label]) => (
              <button key={k} onClick={() => setFilter(k)}
                className="mono"
                style={{
                  padding: '6px 12px',
                  border: '1px solid ' + (filter === k ? 'var(--live)' : 'var(--line-2)'),
                  borderRadius: 100,
                  background: filter === k ? 'var(--live-dim)' : 'transparent',
                  color: filter === k ? 'var(--live)' : 'var(--fg-2)',
                  fontSize: 11, cursor: 'pointer',
                  letterSpacing: '0.04em',
                }}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '0 28px 80px' }}>
        <div style={{ maxWidth: 880, margin: '0 auto' }}>
          {filtered.map((r, i) => (
            <ReleaseEntry key={r.version} release={r} first={i === 0} />
          ))}
        </div>
      </section>

      <MarketingFooter goTo={goTo} />
    </div>
  );
}

function ReleaseEntry({ release, first }) {
  return (
    <article style={{
      display: 'grid', gridTemplateColumns: '120px 1fr', gap: 32,
      padding: '40px 0', borderBottom: '1px solid var(--line-1)',
      alignItems: 'flex-start',
    }} className="release-entry">
      {/* date column */}
      <div style={{ position: 'sticky', top: 80 }}>
        <div className="mono tiny dim">{release.date}</div>
        <div className="mono" style={{
          fontSize: 18, color: 'var(--fg-0)', marginTop: 4,
          letterSpacing: '-0.01em',
        }}>
          v{release.version}
        </div>
        {first && (
          <span className="tag mono tiny" style={{
            marginTop: 8, borderColor: 'var(--live)', color: 'var(--live)',
          }}>
            <span className="dot live pulse" /> latest
          </span>
        )}
        {release.codename && (
          <div className="mono tiny" style={{
            marginTop: 6, color: 'var(--judge)',
          }}>
            "{release.codename}"
          </div>
        )}
      </div>

      {/* body */}
      <div>
        <h2 style={{
          margin: '0 0 8px', fontSize: 22, fontWeight: 500,
          letterSpacing: '-0.015em',
        }}>
          {release.title}
        </h2>
        {release.summary && (
          <p style={{
            margin: '0 0 20px', fontSize: 14,
            color: 'var(--fg-1)', lineHeight: 1.6, maxWidth: 640,
          }}>
            {release.summary}
          </p>
        )}

        {release.media && (
          <div style={{
            margin: '8px 0 24px',
            border: '1px solid var(--line-2)', borderRadius: 8,
            background: 'var(--bg-1)', overflow: 'hidden',
            padding: 18,
            fontFamily: 'var(--mono)', fontSize: 12,
            color: 'var(--fg-2)', lineHeight: 1.7,
          }}>
            {release.media}
          </div>
        )}

        <ul style={{
          listStyle: 'none', padding: 0, margin: 0,
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          {release.items.map((it, i) => <ChangeItem key={i} item={it} />)}
        </ul>
      </div>
    </article>
  );
}

function ChangeItem({ item }) {
  const kindStyles = {
    feature:  { tag: 'NEW',     color: 'var(--live)' },
    model:    { tag: 'MODEL',   color: 'var(--judge)' },
    fix:      { tag: 'FIX',     color: 'var(--neutral)' },
    breaking: { tag: 'BREAKING',color: 'var(--err)' },
    perf:     { tag: 'PERF',    color: 'var(--pro)' },
  };
  const k = kindStyles[item.kind] || kindStyles.feature;
  return (
    <li style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <span className="mono" style={{
        flexShrink: 0, fontSize: 9, fontWeight: 600,
        padding: '2px 6px', borderRadius: 3,
        border: '1px solid ' + k.color, color: k.color,
        letterSpacing: '0.06em', minWidth: 60, textAlign: 'center',
        marginTop: 2,
      }}>
        {k.tag}
      </span>
      <div style={{ fontSize: 13.5, color: 'var(--fg-1)', lineHeight: 1.55 }}>
        <span style={{ color: 'var(--fg-0)' }}>{item.title}</span>
        {item.body && <span> · {item.body}</span>}
        {item.thanks && (
          <span className="dim mono tiny" style={{ marginLeft: 6 }}>
            ({item.thanks})
          </span>
        )}
      </div>
    </li>
  );
}

const CHANGELOG_ZH = [
  {
    version: '0.4.2', date: '2026-01-19', codename: 'Brazil',
    title: 'Resolution Center 重做 + Replay 双 fork 对比',
    summary: '把投票 / 唱票 / 评委判词 / 自动纪要从 Room 里拆出来, 单独成页。Replay 现在支持 4 个并行 fork 同屏对比。',
    items: [
      { kind: 'feature', title: 'Resolution Center 单独成页', body: '/app/resolution · 投票 → 评委 → 纪要分三阶段' },
      { kind: 'feature', title: 'Replay 4-fork 对比', body: '把 main 与 3 条分支的 transcript 并排比较, 找出关键岔路点' },
      { kind: 'feature', title: 'Context inspector', body: '点任一 agent 发言查看完整 prompt(system / KB / summary / transcript)' },
      { kind: 'fix',     title: '@-mention 解析', body: '修了 @cfo.bran 这种带 dot 的别名识别失败' },
      { kind: 'perf',    title: '流式延迟', body: '首字节从 1.2s → 480ms · 多模型并发 fan-out' },
    ],
  },
  {
    version: '0.4.0', date: '2026-01-05', codename: 'Round Table',
    title: 'Room 三种隐喻 + 议程编辑器',
    summary: 'Room 同一场会议提供三种空间布局: Chat-stream / Round-table / Arena, 用户可随时切换。Agenda 编辑器实时预览每轮谁会发言。',
    items: [
      { kind: 'feature',  title: 'Room · Round-table 视图', body: '6 人围圆桌, 当前发言者中央放大' },
      { kind: 'feature',  title: 'Room · Arena 视图', body: '红蓝阵营分区 + 评委位 + 计分板' },
      { kind: 'feature',  title: 'Agenda 编辑器实时预览', body: '右侧面板显示每轮谁会按什么顺序发言' },
      { kind: 'breaking', title: 'agenda schema v2', body: 'rounds[].instruction 字段从 string 升级为 i18n object · v1 自动迁移' },
      { kind: 'model',    title: 'GPT-5 接入', body: 'OpenAI · 新增 gpt-5 / gpt-5-mini, 支持 reasoning_effort 参数' },
    ],
  },
  {
    version: '0.3.5', date: '2025-12-15',
    title: 'Studio 工坊 + Starter packs',
    summary: 'Agent 创建从「写 JSON」改成「点选 + dry-run 预览」, 6 个 starter pack 一键派生整套班子。',
    items: [
      { kind: 'feature', title: 'Studio · agent 编辑器', body: 'persona / 立场 / 模型 / 工具 / 知识库 五段式表单' },
      { kind: 'feature', title: 'Dry-run 即时预览', body: '不进会议, 直接拿一个 prompt 问这个 agent 会怎么说' },
      { kind: 'feature', title: 'Starter packs', body: '决策会议 / 红蓝对抗 / 苏格拉底 / 头脑风暴 / 多专家会诊 / DAO 提案' },
      { kind: 'fix',     title: '知识库 PDF 重传', body: '修了 PDF > 30 页时的二次上传失败' },
    ],
  },
  {
    version: '0.3.0', date: '2025-11-20', codename: 'BYOK',
    title: 'Bring Your Own Keys + 透明计费',
    summary: '现在你可以贴自己的 Anthropic / OpenAI / Google API key, token 平台收 0% markup, 我们只收 $0.04/min。',
    items: [
      { kind: 'feature', title: 'BYOK · Settings 里贴 key', body: '浏览器端 AES-GCM 加密, 服务端不解密' },
      { kind: 'feature', title: 'Billing 透明', body: '每条发言显示 in/out tokens + 当前 spend' },
      { kind: 'feature', title: '预算上限 + 自动停', body: '触顶后会议保留 transcript, 但不再调用 LLM' },
      { kind: 'breaking',title: '/v1/run 端点重命名', body: '改为 /v1/meetings, 老端点保留 90 天' },
    ],
  },
  {
    version: '0.2.0', date: '2025-10-10',
    title: '公测开放',
    summary: 'Public beta 上线, 邀请制注册, 首批 1000 个开发者已收到邀请码。',
    items: [
      { kind: 'feature', title: '公测', body: '邀请制注册 + 邀请你的朋友功能' },
      { kind: 'feature', title: '6 种调度策略', body: 'round-robin / stance-pair / reactive / moderated / vote / free-form' },
      { kind: 'feature', title: '主持人 / 参会者 / 导演 三视角', body: '同一会议内可随时切换' },
    ],
  },
  {
    version: '0.1.0', date: '2025-08-22',
    title: '🎉 第一次会议 ran',
    summary: '内部 Demo · 4 个 agent 围绕「该不该把首页背景改成黑色」开了一场 13 轮辩论, 最终决定 — 改。',
    items: [
      { kind: 'feature', title: 'Hello, world (and 3 others)', body: 'AgentMeeting 第一次跑通端到端' },
    ],
  },
];

const CHANGELOG_EN = [
  {
    version: '0.4.2', date: '2026-01-19', codename: 'Brazil',
    title: 'Resolution Center revamp + 4-fork Replay',
    summary: 'Voting / tallying / judge verdicts / auto-minutes are now their own page. Replay supports comparing 4 parallel forks side-by-side.',
    items: [
      { kind: 'feature', title: 'Resolution Center page', body: '/app/resolution · vote → judge → minutes in 3 phases' },
      { kind: 'feature', title: 'Replay · 4-fork compare', body: 'main + 3 branches diffed inline' },
      { kind: 'feature', title: 'Context inspector', body: 'click any agent message to see the full assembled prompt' },
      { kind: 'fix',     title: '@-mention parsing', body: 'fixed alias resolution for names containing dots (e.g. @cfo.bran)' },
      { kind: 'perf',    title: 'streaming latency', body: 'TTFB 1.2s → 480ms via multi-model fan-out' },
    ],
  },
  {
    version: '0.4.0', date: '2026-01-05', codename: 'Round Table',
    title: 'Three Room metaphors + agenda editor',
    summary: 'Room ships three spatial layouts — chat-stream / round-table / arena — switchable mid-meeting. Agenda editor previews exactly who will speak each round.',
    items: [
      { kind: 'feature',  title: 'Room · round-table view', body: '6 seats, active speaker scaled-up at center' },
      { kind: 'feature',  title: 'Room · arena view', body: 'red/blue camps + judge stand + scoreboard' },
      { kind: 'feature',  title: 'Agenda editor preview', body: 'right rail shows speaker order per round' },
      { kind: 'breaking', title: 'agenda schema v2', body: 'rounds[].instruction is now an i18n object · v1 migrated automatically' },
      { kind: 'model',    title: 'GPT-5 support', body: 'gpt-5 / gpt-5-mini · reasoning_effort param exposed' },
    ],
  },
  {
    version: '0.3.5', date: '2025-12-15',
    title: 'Studio + Starter packs',
    summary: 'Creating an agent went from "write JSON" to "fill a form + dry-run preview". Six starter packs ship a whole panel in one click.',
    items: [
      { kind: 'feature', title: 'Studio · agent editor', body: '5-section form: persona / stance / model / tools / KB' },
      { kind: 'feature', title: 'Dry-run preview', body: 'ask the agent a one-shot question without entering a meeting' },
      { kind: 'feature', title: 'Starter packs', body: 'Decision meeting / Red-blue / Socratic / Brainstorm / Multi-expert / DAO proposal' },
      { kind: 'fix',     title: 'PDF re-upload', body: 'fixed second upload failing when PDF > 30 pages' },
    ],
  },
  {
    version: '0.3.0', date: '2025-11-20', codename: 'BYOK',
    title: 'Bring Your Own Keys + transparent billing',
    summary: 'Drop in your own Anthropic / OpenAI / Google keys — we charge 0% on tokens and only $0.04/min in meeting time.',
    items: [
      { kind: 'feature', title: 'BYOK in Settings', body: 'AES-GCM encrypted in browser, server stores ciphertext only' },
      { kind: 'feature', title: 'Per-message billing surfaced', body: 'in/out tokens + running spend on every turn' },
      { kind: 'feature', title: 'Budget caps + auto-pause', body: 'on cap, transcript is preserved but no further LLM calls' },
      { kind: 'breaking',title: '/v1/run renamed → /v1/meetings', body: 'old endpoint kept for 90 days' },
    ],
  },
  {
    version: '0.2.0', date: '2025-10-10',
    title: 'Public beta',
    summary: 'Public beta opened (invite-only). First 1000 developers received invite codes.',
    items: [
      { kind: 'feature', title: 'Public beta', body: 'invite-only signup + invite-a-friend' },
      { kind: 'feature', title: 'Six scheduling strategies', body: 'round-robin / stance-pair / reactive / moderated / vote / free-form' },
      { kind: 'feature', title: 'Three viewer roles', body: 'host / participant / director — switch mid-meeting' },
    ],
  },
  {
    version: '0.1.0', date: '2025-08-22',
    title: '🎉 first meeting ran',
    summary: 'Internal demo. 4 agents debated "should we paint the homepage black?" for 13 rounds. Verdict: yes.',
    items: [
      { kind: 'feature', title: 'Hello, world (and 3 others)', body: 'first end-to-end meeting on AgentMeeting' },
    ],
  },
];
